import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, ArrowRight, ArrowLeft } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

export default function Reels() {
  const { t, isRussian, currentLanguage } = useTranslation();
  
  // Функция для получения правильного пути в зависимости от языка
  const getLocalizedPath = (path) => {
    if (currentLanguage === 'ru') {
      return path === '/' ? '/ru' : `/ru${path}`;
    }
    return path;
  };
  
  const scrollContainerRef = useRef(null);
  const [playingVideo, setPlayingVideo] = React.useState(null);
  const [videoWithSound, setVideoWithSound] = React.useState(null);
  const [autoPlayEnabled, setAutoPlayEnabled] = React.useState(false);

  // Автоматическое воспроизведение всех видео без звука при загрузке
  useEffect(() => {
    if (!isRussian) return; // Выходим если не русский язык
    
    const timer = setTimeout(() => {
      // Включаем автоматическое воспроизведение всех видео
      setAutoPlayEnabled(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, [isRussian]);

  // Автоматическое воспроизведение при прокрутке
  const handleScroll = () => {
    if (!isRussian || !scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;
    
    // Находим видео, которое находится в центре экрана
    const videoCards = container.querySelectorAll('.video-card');
    let centerVideo = null;
    let minDistance = Infinity;
    
    videoCards.forEach((card, index) => {
      const cardRect = card.getBoundingClientRect();
      const cardCenter = cardRect.left + cardRect.width / 2;
      const distance = Math.abs(cardCenter - containerCenter);
      
      if (distance < minDistance && cardRect.left >= containerRect.left && cardRect.right <= containerRect.right) {
        minDistance = distance;
        centerVideo = index + 1;
      }
    });
    
    if (centerVideo && centerVideo !== playingVideo) {
      setPlayingVideo(centerVideo);
      // Всегда без звука при автоматическом переключении
      setVideoWithSound(null);
    }
  };

  useEffect(() => {
    if (!isRussian) return; // Выходим если не русский язык
    
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [playingVideo, isRussian]);

  // Показываем секцию только на русском языке
  if (!isRussian) {
    return null;
  }

  const reels = [
    {
      id: 1,
      title: t('reels.client_result.title'),
      description: t('reels.client_result.description'),
      duration: "0:45",
      videoUrl: "/videos/reels/reel1.mp4",
      thumbnail: "/images/reels/reel1-thumb.jpg"
    },
    {
      id: 2,
      title: t('reels.work_process.title'),
      description: t('reels.work_process.description'),
      duration: "1:20",
      videoUrl: "/videos/reels/reel2.mp4",
      thumbnail: "/images/reels/reel2-thumb.jpg"
    },
    {
      id: 3,
      title: t('reels.client_review.title'),
      description: t('reels.client_review.description'),
      duration: "2:15",
      videoUrl: "/videos/reels/reel3.mp4",
      thumbnail: "/images/reels/reel3-thumb.jpg"
    },
    {
      id: 4,
      title: t('reels.before_after.title'),
      description: t('reels.before_after.description'),
      duration: "1:30",
      videoUrl: "/videos/reels/reel4.mp4",
      thumbnail: "/images/reels/reel4-thumb.jpg"
    }
  ];

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -320,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 320,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="py-16 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Заголовок секции */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-dark-900">
            {t('reels.title')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            {t('reels.subtitle')}
          </p>
        </motion.div>

        {/* Контейнер для прокрутки */}
        <div className="relative">
          {/* Кнопки навигации - скрыты на мобильных */}
          <button
            onClick={scrollLeft}
            className="hidden sm:block absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-gray-100 hover:bg-gray-200 text-gray-700 p-3 rounded-full transition-all duration-300 shadow-lg"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          
          <button
            onClick={scrollRight}
            className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-gray-100 hover:bg-gray-200 text-gray-700 p-3 rounded-full transition-all duration-300 shadow-lg"
          >
            <ArrowRight className="h-6 w-6" />
          </button>

          {/* Горизонтальная прокрутка */}
          <div
            ref={scrollContainerRef}
            className="flex gap-4 sm:gap-6 overflow-x-auto pb-4 scrollbar-hide"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {reels.map((reel, index) => (
              <motion.div
                key={reel.id}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex-shrink-0 w-72 sm:w-80 video-card"
              >
                <div className="relative group cursor-pointer">
                  {/* Видео карточка */}
                  <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 hover:scale-105">
                    {/* Превью видео */}
                    <div className="relative aspect-[9/16] bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                      {autoPlayEnabled ? (
                        // Видео плеер
                        <video
                          className="w-full h-full object-cover rounded-2xl"
                          autoPlay
                          loop
                          muted={videoWithSound !== reel.id}
                          playsInline
                          onClick={() => {
                            // При клике переключаем звук
                            if (videoWithSound === reel.id) {
                              setVideoWithSound(null); // Выключаем звук
                            } else {
                              setVideoWithSound(reel.id); // Включаем звук
                            }
                          }}
                        >
                          <source src={reel.videoUrl} type="video/mp4" />
                          Ваш браузер не поддерживает видео.
                        </video>
                      ) : (
                        // Превью с thumbnail и кнопкой воспроизведения
                        <div className="relative w-full h-full cursor-pointer" onClick={() => {
                          setPlayingVideo(reel.id);
                          setVideoWithSound(null);
                        }}>
                          {/* Thumbnail изображение */}
                          <img 
                            src={reel.thumbnail} 
                            alt={reel.title}
                            className="w-full h-full object-cover rounded-2xl"
                            onError={(e) => {
                              // Если изображение не загрузилось, показываем градиент
                              e.target.style.display = 'none';
                            }}
                          />
                          
                          {/* Оверлей с кнопкой Play */}
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors duration-300">
                            <div className="text-center">
                              <div className="w-20 h-20 bg-rose-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-rose-700 transition-colors duration-300 shadow-lg">
                                <Play className="h-8 w-8 text-white ml-1" />
                              </div>
                              <p className="text-white text-sm font-medium">Нажмите для воспроизведения</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Длительность */}
                      {playingVideo !== reel.id && (
                        <div className="absolute bottom-4 right-4 bg-gray-800/80 text-white px-2 py-1 rounded text-sm">
                          {reel.duration}
                        </div>
                      )}
                      
                      {/* Индикатор звука */}
                      {playingVideo === reel.id && videoWithSound === reel.id && (
                        <div className="absolute top-4 right-4 bg-rose-600 text-white px-2 py-1 rounded text-xs">
                          🔊
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Призыв к действию */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-gray-600 mb-6 text-sm sm:text-base">
            {t('reels.cta.description')}
          </p>
          <a
            href={getLocalizedPath("/contact")}
            className="inline-flex items-center gap-3 bg-rose-600 hover:bg-rose-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base"
          >
            {t('reels.cta.button')}
            <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
          </a>
        </motion.div>
      </div>

    </section>
  );
}
